import 'setimmediate';

import { css, Global } from '@emotion/core';
import { EntityActions, IGenericEntities } from '@travelnest/entities/build';
import { theme } from '@travelnest/hui';
import { BodyOverlayCSS } from '@travelnest/merlin.modal';
import { TaskBasedProperty } from '@travelnest/task-based-property';
import { TaskBasedProperty as TaskBasedPropertyPortal } from '@travelnest/task-based-property-portal';

import * as React from 'react';
import { connect, useDispatch, useStore } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { IProperty } from './data/entities/property';
import { actions as entityActions } from './data/store';
import { stopImpersonatingUser } from './entities/admin/actions';
import { UserGroups } from './entities/auth/constants';
import { currentUserEmail, currentUserId, currentUserName, currentUserPhone, currentUserValidated } from './entities/auth/session/selectors';
import { matchTbpMode, redirectPathToTBPEdit } from './helpers';
import { logging } from './logging';
import { IThunkDispatch } from './store/middleware';
import { deregisterListener, registerListener } from './store/middleware/pubsub';
import { IState } from './store/reducer';
import { AdminFrame } from './view/components/admin-frame/AdminFrame';
import { withAdminOnly } from './view/components/auth/AdminAccess';
import { withAuthentication } from './view/components/auth/Auth';
import { ExternalRedirect } from './view/components/external-redirect/ExternalRedirect';
import { FreshChatWidget } from './view/components/freshchat/FreshChatWidget';
import { Loader } from './view/components/loader/Loader';
import { IOverlayClass } from './view/components/overlay-container/Overlay';
import { OverlayContainer } from './view/components/overlay-container/OverlayContainer';
import { withProductFrame } from './view/components/product-frame/ProductFrame';
import { useCanonicalId } from './view/helpers/hooks';
import { Action } from './view/pages/actions/details/ActionDetailsContainer';
import BookingComHelperContainer from './view/pages/admin/booking-com/BookingComHelperContainer';
import { OwnerBookings } from './view/pages/bookings/OwnerBookingsContainer';
import { OwnerBookingsList } from './view/pages/bookings/OwnerBookingsListContainer';
import { setCalendarPersistence } from './view/pages/calendar/actions';
import NextGenCalendarPage, { calendarPrintingStyles } from './view/pages/calendar/CalendarPage';
import { CalendarBundlePP } from './view/pages/calendar/CalendarPP';
import { Customer } from './view/pages/customer/CustomerContainer';
import { ErrorPage } from './view/pages/error/ErrorPage';
import { Home } from './view/pages/home/HomeContainer';
import { Login } from './view/pages/login/LoginContainer';
import { ResetPassword } from './view/pages/login/reset/ResetPasswordContainer';
import { ResetSuccess } from './view/pages/login/reset/ResetSuccess';
import { ResetVerifyPassword } from './view/pages/login/reset/ResetVerifyPasswordContainer';
import { Logout } from './view/pages/logout/Logout';
import { MarketingRoutes } from './view/pages/marketing/Index';
import More from './view/pages/more';
import { NotFound } from './view/pages/not-found/NotFound';
import { PayLater } from './view/pages/pay-later/PayLater';
import { PropertyScorePage, PropertyScores } from './view/pages/property-score';
import { PropertyBookings } from './view/pages/property/bookings/details/PropertyBookingContainer';
import { PropertyBookingList } from './view/pages/property/bookings/list/PropertyBookingsListContainer';
import { PropertyEditPhotos } from './view/pages/property/edit/photos/PropertyEditPhotosContainer';
import { PropertyList } from './view/pages/property/list/PropertyListContainer';

interface IMainComponentProps {
	showAdminFrame: boolean;
	showOverlay: IOverlayClass;
	hasError: boolean;
	onErrorDisplayed: () => any;
	customerId: string;
	customerName: string;
	customerEmail: string;
	customerValidated: boolean;
	customerPhone: string;
}

// Lazy loading pages for Admin chunk
const AdminHome = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/home/AdminHomeContainer'));
const AdminPropertyView = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/property/view/AdminPropertyViewContainer'));
const AdminPricingView = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/pricing/view/AdminPricingViewContainer'));
const AdminListingEdit = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/listing/edit/AdminListingEditContainer'));
const AdminCustomerListings = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/customer/listings/AdminCustomerListingsContainer'));
const AdminCustomerEdit = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/customer/edit/AdminCustomerEditContainer'));
const AdminBookingCreate = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/bookings/create/BookingCreateContainer'));
const AdminSignup = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/register/admin/AdminSignupContainer'));
const AdminConfirm = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/register/admin-confirm/AdminConfirmContainer'));
const AdminSyncTools = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/sync/AdminSyncTools'));
const AirbnbLinkingTools = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/sync/AirbnbLinkingTools'));
const FinanceTools = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/finance/FinanceTools'));
const AdminPromotionTools = React.lazy(() => import(/* webpackChunkName: "admin" */ './view/pages/admin/sync/AdminPromotionTools'));

// convert component prop of authenticated Routes
// into JSX from base js functions to prevent unneccessary unmounting
const HomeRoute = withProductFrame(withAuthentication(Home));
const PropertyListRoute = withProductFrame(withAuthentication(PropertyList));
const PropertyBookingListRoute = withProductFrame(withAuthentication(PropertyBookingList));
const PropertyBookingsRoute = withProductFrame(withAuthentication(PropertyBookings));
const OwnerBookingsRoute = withProductFrame(withAuthentication(OwnerBookings));
const OwnerCalendarsRoute = withProductFrame(withAuthentication(NextGenCalendarPage));
const OwnerBookingsListRoute = withProductFrame(withAuthentication(OwnerBookingsList));
const CustomerRoute = withProductFrame(withAuthentication(Customer));
const MarketingRoutesRoute = withProductFrame(withAuthentication(MarketingRoutes));
const NotFoundRoute = withProductFrame(withAuthentication(NotFound));
const GenericErrorRoute = withProductFrame(ErrorPage);
const PropertyScoresRoute = withProductFrame(withAuthentication(PropertyScores));
const PropertyScoreForPropertyRoute = withProductFrame(withAuthentication(PropertyScorePage));
const ActionRoute = withProductFrame(withAuthentication(Action));
const MoreRoute = withProductFrame(withAuthentication(More));

// Admin Routes
const AdminBookingCreateRoute = withProductFrame(withAdminOnly(AdminBookingCreate));
const AdminCustomerListingsRoute = withProductFrame(withAdminOnly(AdminCustomerListings));
const AdminCustomerEditRoute = withProductFrame(withAdminOnly(AdminCustomerEdit));
const AdminListingEditRoute = withProductFrame(withAdminOnly(AdminListingEdit));
const AdminPricingViewRoute = withProductFrame(withAdminOnly(AdminPricingView));
const BookingComHelperRoute = withProductFrame(withAdminOnly(BookingComHelperContainer));
const AdminPropertyViewRoute = withProductFrame(withAdminOnly(AdminPropertyView));
const AdminSyncToolsRoute = withProductFrame(withAdminOnly(AdminSyncTools));
const AirbnbLinkingToolsRoute = withProductFrame(withAdminOnly(AirbnbLinkingTools));
const AdminPromotionToolsRoute = withProductFrame(withAdminOnly(AdminPromotionTools));
const AdminFinanceToolsRoute = withProductFrame(withAdminOnly(FinanceTools));
const AdminHomeRoute = withProductFrame(withAdminOnly(AdminHome));
/**
 * HOC which redirects users into the correct mode of Task-Based Property if they attempt to launch into one that doesn't match
 * their state.
 * Extracted to a named component to avoid the re-mounting behaviour described in: https://v5.reactrouter.com/web/api/Route/component
 */
const TBPRouteWrapper = withProductFrame(withAuthentication(() => {
	const store = useStore<IState, EntityActions<IGenericEntities>>();
	const location = useLocation();
	const mode = matchTbpMode(location.pathname);
	const params = useParams<{ id: string }>();
	const { loading, id, legacy, errored } = useCanonicalId(params.id!);

	const customerId = currentUserId(store.getState());
	const isAdminRole = store.getState().entities.auth.session.current.groups.includes(UserGroups.ADMIN);

	// Redirect anyone who isn't an admin who somehow lands on a TBP property page to Property Portal
	if (!isAdminRole) {
		window.location.assign(`${window.location.origin}/property/${params.id}/overview`);
		return <Loader loading={true} />;
	}

	if (loading) {
		return <Loader loading={true} />;
	}

	const shouldRedirect = params.id === legacy;
	if (shouldRedirect) {
		return errored ? <Navigate to={'/not-found'} /> : <Navigate to={location.pathname.replace(params.id, String(id))} />;
	}

	// Where we have no mode, pick 'edit' as a default.
	if (!mode) {
		const nextUrl = redirectPathToTBPEdit(params.id!, location.pathname);
		return <Navigate to={nextUrl} />;
	}

	const readOnlyWarning = () => <p>TBP now read-only: please use Property Portal/impersonation to view</p>;

	return <TaskBasedProperty
		baseURL={location.pathname}
		mode={mode}
		actions={entityActions}
		store={store}
		propertyId={id as number}
		customerId={customerId}
		photosTask={readOnlyWarning}
		availabilityTask={readOnlyWarning}
		registerActionListener={registerListener}
		deregisterActionListener={deregisterListener}
	/>;
}));

const TBPStandalone = withAuthentication(() => {
	const store = useStore<IState, EntityActions<IGenericEntities>>();
	const location = useLocation();
	const mode = matchTbpMode(location.pathname) ?? 'edit';
	const params = useParams<{ id: string }>();
	const { loading, id } = useCanonicalId(params.id!);

	if (loading) {
		return <Loader loading={true} />;
	}

	return <TaskBasedPropertyPortal
		baseURL={location.pathname}
		mode={mode}
		actions={entityActions}
		store={store}
		propertyId={id as number}
		customerId={currentUserId(store.getState())}
		photosTask={(propertyId: number) => <PropertyEditPhotos buttonContext={'merlin'} property={{ id: propertyId } as IProperty} />}
		availabilityTask={
			(propertyId: number) =>
				<CalendarBundlePP
					propertyId={id!}
					loadingProperties={false}
					initialMonth={store.getState().views.calendar.persistence?.currentMonth}
				/>
		}
		registerActionListener={registerListener}
		deregisterActionListener={deregisterListener}
	/>;
});

const _404 = withProductFrame(NotFound);

const RedirectPortalNext = withAuthentication(() => {
	const location = useLocation();

	// Remove the first part of the url path
	// e.g. /redirect/property/1234/listings -> /property/1234/listings
	const path = location.pathname.split('/').slice(2).join('/');

	window.location.assign(`${window.location.origin}/${path}`);
	return null;
});

const RedirectListings = withAuthentication(() => {
	const params = useParams();
	const propertyId = params.id ? params.id : '';
	window.location.assign(`${window.location.origin}/property/${propertyId}/listings`);
	return null;
});

const RedirectPropertyAdmin = withAuthentication(() => {
	const params = useParams();
	const propertyId = params.propertyId ? params.propertyId : '';
	window.location.assign(`${window.location.origin}/admin/property/${propertyId}/listings`);
	return null;
});

const RedirectBookingcomAdmin = withAuthentication(() => {
	window.location.assign(`${window.location.origin}/admin/channel/bookingcom`);
	return null;
});

const RedirectPropertyDetails = withAuthentication(() => {
	const params = useParams();
	window.location.assign(
		`${window.location.origin}/property/${params.id}/details/${params['*']}`
	);
	return null;
});

const Routings: React.FC = () => {
	return <React.Suspense fallback={<Loader />}>
		<Routes>
			<Route path="/" element={<HomeRoute />} />

			<Route path="/action/:actionType/:id" element={<ActionRoute />} />
			<Route path="/login/*" element={<Login />} />
			<Route path="/logout" element={<Logout />} />
			<Route path="/reset" element={<ResetPassword />} />
			<Route path="/reset-new" element={<ResetVerifyPassword />} />
			<Route path="/reset-success" element={<ResetSuccess />} />

			<Route path="/properties" element={<PropertyListRoute />} />
			<Route path="/properties/add/property" element={<PropertyListRoute showModalByDefault={true} />} />
			<Route path="/property/:id/bookings" element={<PropertyBookingListRoute />} />
			<Route path="/property/:id/listings" element={<RedirectListings />} />
			<Route path="/property/:property_id/booking/:booking_id/" element={<PropertyBookingsRoute />} />

			{/*task-based property routes*/}
			<Route path="/tbp/:id/*" element={<TBPStandalone />} />
			<Route path="/property/:id/add/*" element={<TBPRouteWrapper />} />
			<Route path="/property/:id/edit/*" element={<TBPRouteWrapper />} />
			<Route path="/property/:id/onboard/*" element={<TBPRouteWrapper />} />
			<Route path="/property/:id" element={<TBPRouteWrapper />} />

			{/* redirect old TBP to onboarding, as it was only ever serving the onboarding flow */}
			<Route
				path="/newproperty/:id"
				Component={() => {
					const params = useParams();
					return <Navigate to={`/property/${params.id}/onboard`} />;
				}}
			/>
			<Route
				path="/newproperty/:id/*"
				Component={() => {
					const params = useParams();
					return <Navigate to={`/property/${params.id}/onboard`} />;
				}}
			/>
			<Route path="/property-scores" element={<PropertyScoresRoute />} />
			<Route path="/property-scores/:id/score" element={<PropertyScoreForPropertyRoute />} />
			<Route path="/bookings/*" element={<OwnerBookingsRoute />} />
			<Route path="/calendar/:id?" element={<OwnerCalendarsRoute />} />
			<Route path="/customer/:id/bookings" element={<OwnerBookingsListRoute />} />

			{/* Microsite redirect routes for authentication purposes */}
			<Route
				path="/redirect/*"
				element={<RedirectPortalNext />}
			/>
			<Route
				path="/admin/next/property/:propertyId/listings"
				element={<RedirectPropertyAdmin />}
			/>

			<Route path="/account/*" element={<CustomerRoute />} />

			<Route path="/pay_later/:eventId?" element={<PayLater />} />

			<Route path="/marketing/*" element={<MarketingRoutesRoute />} />
			<Route path="/preferences/*" Component={() => <Navigate to="/channels" />} />

			<Route path="/register/admin" element={<AdminSignup />} />
			<Route path="/register/admin-confirm" element={<AdminConfirm />} />

			<Route path="/admin/bookings/property/:id/create" element={<AdminBookingCreateRoute />} />
			<Route path="/admin/customer/:id/listings" element={<AdminCustomerListingsRoute />} />
			<Route path="/admin/customer/:id/edit" element={<AdminCustomerEditRoute />} />
			<Route path="/admin/property/:property_id/listing/:id/edit" element={<AdminListingEditRoute />} />
			<Route path="/admin/property/:id/pricing" element={<AdminPricingViewRoute />} />
			<Route path="/admin/property/:id/booking-com-helper" element={<BookingComHelperRoute />} />
			<Route path="/admin/property/:id" element={<AdminPropertyViewRoute />} />
			<Route
				path="/property/:id/details/*"
				element={<RedirectPropertyDetails/>}
				/>
			<Route path="/admin/sync-tools" element={<AdminSyncToolsRoute />} />
			<Route path="/admin/airbnb-linking-tools" element={<AirbnbLinkingToolsRoute />} />
			<Route path="/admin/channel/bookingcom" element={<RedirectBookingcomAdmin />} />
			<Route path="/admin/sync-promotion-tools" element={<AdminPromotionToolsRoute />} />
			<Route path="/admin/finance-tools" element={<AdminFinanceToolsRoute />} />
			<Route path="/admin/*" element={<AdminHomeRoute />} />

			<Route path="/not-found/" element={<NotFoundRoute />} />
			{/* We've remove our old sign-up flow, but can't guarantee it's not still linked to */}
			{/* This re-directs all routes to FTUX */}
			<Route path="/onboard/" element={<ExternalRedirect path="/signup" />} />

			<Route path="/more" element={<MoreRoute />} />
			<Route path="*" element={<_404 />} />
		</Routes>
	</React.Suspense>;
};

interface IErrorBoundaryProps { children?: React.ReactNode; }
interface IErrorBoundaryState { hasError: boolean; error: any; info: any; }

class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
	constructor(props: IErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null, info: null };
	}

	public componentDidCatch(error: any, info: any) {
		console.error(error, info);
		logging.error(error, info);
		this.setState({ hasError: true, error, info });
	}

	public render() {
		if (this.state.hasError) {
			// You can render any custom fallback UI
			return <GenericErrorRoute error={this.state.error} info={this.state.info} />;
		}

		return this.props.children;
	}
}

export const MainComponent = (props: IMainComponentProps) => {
	const location = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	React.useEffect(() => {
		window.scroll(0, 0);
		dispatch({ type: 'APP_LOCATION_CHANGE' });
	}, [location]);

	const exitAdminFrame = () => {
		dispatch(stopImpersonatingUser());
		dispatch(setCalendarPersistence({ propertyId: undefined, currentMonth: new Date() }));
		navigate('/admin');
	};

	const { showAdminFrame, showOverlay } = props;
	let page = <>
		<Routings />
		{/* The global component here is for the merlin modal css issues we keep fighting. Actual fix is get merlin sorted. */}
		<Global
			styles={
				css`
					body {
						font-family: ${theme.typography.IR110.fontFamily};
						font-weight: ${theme.typography.IR110.fontWeight};
						letter-spacing: ${theme.typography.IR110.letterSpacing};
    					color: ${theme.colors.typography.body};
						background-color: #FFFFFF;
					}
						/* Modal related overrides */
						${BodyOverlayCSS}

						.calendar-education-modal {
							max-height: 600px;
							.travelnest-modal__content {
								padding: unset !important;
								max-width: 100% !important;
							}
						}
						.calendar-sync-modal {
							max-height: 680px;
							.travelnest-modal__content {
								padding: unset !important;
								max-width: 100% !important;
							}
						}

						/* Styles for print-outs of CalendarPage */
						${calendarPrintingStyles}
					`
			}
		/>
		<FreshChatWidget
			id={props.customerId}
			name={props.customerName}
			email={props.customerEmail}
			validated={props.customerValidated}
			phoneNumber={props.customerPhone}
		/>
	</>;
	if (showAdminFrame) {
		page = <AdminFrame onExit={exitAdminFrame}>{page}</AdminFrame>;
	}
	if (showOverlay) {
		page = <OverlayContainer overlay={showOverlay}>{page}</OverlayContainer>;
	}
	return <ErrorBoundary>{page}</ErrorBoundary>;
};

const mapStateToProps = (state: IState) => ({
	showAdminFrame: state.entities.admin.isImpersonating,
	showOverlay: state.views.overlay.overlays.slice(-1)[0],
	hasError: !!state.views.globalError,
	customerId: currentUserId(state),
	customerName: currentUserName(state),
	customerEmail: currentUserEmail(state),
	customerValidated: currentUserValidated(state),
	customerPhone: currentUserPhone(state)
});

const mapDispatchToProps = (dispatch: IThunkDispatch) => ({
	onErrorDisplayed: () => dispatch({ type: 'GLOBAL_ERROR_SET' }),
});

// tslint:disable-next-line: max-file-line-count
export const Main = connect(mapStateToProps, mapDispatchToProps)(MainComponent);
